import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { fetchCallsByProspect } from '../../../api/calls';
import PagesJaunesEnrichModal, {
  canEnrichProspect,
  isProspectEnriched,
} from './PagesJaunesEnrichModal';
import { campaignsForProspect } from '../../../utils/campaignProspects';
import { ProspectCustomFieldsDisplay } from './ProspectCustomFieldsEditor';

function EnrichedHint({ contact, className = '' }) {
  if (!isProspectEnriched(contact)) return null;
  return (
    <span className={`text-xs text-muted-foreground whitespace-nowrap ${className}`}>
      (Déjà enrichi)
    </span>
  );
}

const ContactCard = ({
  contact,
  campaigns = [],
  viewMode,
  onClaim,
  onEdit,
  onCall,
  onMail,
  onEnriched,
  mailDisabled = false,
}) => {
  const matchedCampaigns = useMemo(
    () => campaignsForProspect(contact?.tags, campaigns),
    [contact?.tags, campaigns],
  );
  const [showModal, setShowModal] = useState(false);
  const [showPagesJaunes, setShowPagesJaunes] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);

  const outcomeLabels = {
    success: 'Succès',
    'no-answer': 'Pas de réponse',
    voicemail: 'Messagerie',
    callback: 'Rappel requis',
    'not-interested': 'Pas intéressé',
    'wrong-number': 'Mauvais numéro',
  };

  const getOutcomeColor = (outcome) => {
    const colors = {
      success: 'text-success',
      'no-answer': 'text-warning',
      voicemail: 'text-secondary',
      callback: 'text-accent',
      'not-interested': 'text-error',
      'wrong-number': 'text-muted-foreground',
    };
    return colors?.[outcome] || 'text-muted-foreground';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor((seconds || 0) / 60);
    const secs = (seconds || 0) % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const loadCalls = async () => {
      if (!showModal || !contact?.id) return;
      try {
        setLoadingCalls(true);
        const res = await fetchCallsByProspect(contact.id);
        const calls = res?.data?.calls || [];
        setCallHistory(calls);
      } catch (e) {
        console.error('Failed to load call history', e);
        setCallHistory([]);
      } finally {
        setLoadingCalls(false);
      }
    };

    loadCalls();
  }, [showModal, contact?.id]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error border-error/20';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'low':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'prospect':
        return 'text-blue-500 border-blue-500/20s';
      case 'qualifie':
        return 'text-success border-success/20';
      case 'proposition':
        return 'text-warning border-warning/20';
      case 'negociation':
        return 'text-orange-500 border-orange-500/20';
      case 'conclu':
        return 'text-emerald-600 border-emerald-600/20';
      case 'non_interesse':
        return 'text-muted-foreground border-muted-foreground/20';
      default:
        return 'text-muted-foreground border-muted-foreground/20';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'prospect':
        return 'Prospect';
      case 'qualifie':
        return 'Qualifié';
      case 'proposition':
        return 'Proposition';
      case 'negociation':
        return 'Négociation';
      case 'conclu':
        return 'Conclu';
      case 'non_interesse':
        return 'Non intéressé';
      default:
        return status;
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'Haute';
      case 'medium':
        return 'Moyenne';
      case 'low':
        return 'Basse';
      default:
        return priority;
    }
  };

  const isPhoneValid = (phone) => {
    if (!phone) return false;
    return /^\+?[0-9\s-]+$/.test(phone);
  };

  const getCallButtonVariant = (c) => {
    const phone = c?.phone;
    const hasValidPhone = isPhoneValid(phone);
    const hasCallback = !!c?.callbackAt;
    const hasBeenCalled =
      !!c?.lastContact && c.lastContact !== 'Aucun rappel programmé';

    if (!hasValidPhone) {
      // Rouge : numéro invalide / incorrect
      return 'danger';
    }

    if (hasCallback) {
      // Orange : rappel programmé
      return 'warning';
    }

    if (hasBeenCalled) {
      // Vert : déjà appelé au moins une fois
      return 'success';
    }

    // Gris : jamais appelé
    return 'secondary';
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const renderClaimInfo = () => {
    if (contact?.claimedByEmail) {
      return (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Icon name="UserCheck" size={12} />
          <span>Assigné à {contact.claimedByEmail}</span>
        </div>
      );
    }

    return (
      <div className='flex flex-wrap items-center gap-3 text-xs text-muted-foreground'>
        <span className="text-primary transition-smooth line-clamp-1">
          Client non assigné
        </span>
      </div>
      
    );
  };

  if (viewMode === 'list') {
    return (
      <>
        <div className="bg-card rounded-lg p-4 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth border border-border">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">

              <div className="flex-1 min-w-0">
              <div className="mb-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground line-clamp-1 flex-1">
                    {contact?.name}
                  </h3>
                </div>
                <div className="mt-1 flex items-center gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(contact?.status)}`}>
                    {getStatusLabel(contact?.status)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(contact?.priority)}`}>
                    {getPriorityLabel(contact?.priority)}
                  </span>
                </div>
              </div>
                {[contact?.position, contact?.company].filter(Boolean).length > 0 ? (
                  <p className="text-sm text-muted-foreground line-clamp-1 mb-1">
                    {[contact?.position, contact?.company].filter(Boolean).join(' • ')}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Icon name="Phone" size={12} />
                    {contact?.phone}
                  </span>
                  {contact?.mobilePhone && (
                    <span className="flex items-center gap-1">
                      <Icon name="Smartphone" size={12} />
                      {contact.mobilePhone}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Icon name="Mail" size={12} />
                    {contact?.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    {contact?.lastContact}
                  </span>
                  {renderClaimInfo()}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={() => (onCall ? onCall(contact) : handleCall(contact?.phone))}
                variant="success"
                size="sm"
                iconName="Phone"
                className="flex-1 md:flex-none"
              >
                Fixe
              </Button>
              {contact?.mobilePhone && (
                <Button
                  onClick={() =>
                    onCall
                      ? onCall({ ...contact, phone: contact.mobilePhone })
                      : handleCall(contact.mobilePhone)
                  }
                  variant="success"
                  size="sm"
                  iconName="Smartphone"
                  className="flex-1 md:flex-none"
                >
                  Mobile
                </Button>
              )}
              {!contact?.claimedByEmail && (
                <Button
                  onClick={() => onClaim?.(contact.id)}
                  variant="outline"
                  size="sm"
                  iconName="UserPlus"
                >
                  S&apos;assigner le prospect
                </Button>
              )}

              <Button
                onClick={onEdit}
                variant="ghost"
                size="sm"
                iconName="Pencil"
              />
              <Button
                onClick={() => onMail?.(contact)}
                variant="ghost"
                size="sm"
                iconName="Mail"
                disabled={mailDisabled}
                title={mailDisabled ? 'Quota e-mail épuisé' : 'Envoyer un email'}
              />
              {canEnrichProspect(contact) && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    onClick={() => setShowPagesJaunes(true)}
                    variant="ghost"
                    size="sm"
                    iconName="Search"
                    title="Enrichir le prospect"
                  />
                  <EnrichedHint contact={contact} className="hidden xl:inline" />
                </div>
              )}
              <Button
                onClick={() => setShowModal(true)}
                variant="ghost"
                size="sm"
                iconName="Eye"
              />

            </div>
          </div>
        </div>

        {showModal && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          >
            <div
              className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between border-b border-border p-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">
                    {contact?.name}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {getStatusLabel(contact?.status)} • {getPriorityLabel(contact?.priority)}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Icon name="Phone" size={14} className="text-muted-foreground" />
                    <span>{contact?.phone}</span>
                  </div>
                  {contact?.mobilePhone && (
                    <div className="flex items-center gap-2">
                      <Icon name="Smartphone" size={14} className="text-muted-foreground" />
                      <span>{contact?.mobilePhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={14} className="text-muted-foreground" />
                    <span>{contact?.email}</span>
                  </div>
                  {contact?.website && (
                    <div className="flex items-center gap-2">
                      <Icon name="Globe2" size={14} className="text-muted-foreground" />
                      <a
                        href={contact.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline break-all"
                      >
                        {contact.website}
                      </a>
                    </div>
                  )}
                  {contact?.ape && (
                    <div className="flex items-center gap-2">
                      <Icon name="Building2" size={14} className="text-muted-foreground" />
                      <span>
                        Code APE : {contact.ape}
                      </span>
                    </div>
                  )}
                  {contact?.siret && (
                    <div className="flex items-center gap-2">
                      <Icon name="Hash" size={14} className="text-muted-foreground" />
                      <span>SIRET : {contact.siret}</span>
                    </div>
                  )}
                  {(contact?.address || contact?.postalCode || contact?.city) && (
                    <div className="flex items-start gap-2">
                      <Icon name="MapPin" size={14} className="text-muted-foreground mt-0.5" />
                      <div className="text-xs text-foreground space-y-0.5">
                        {contact?.address && <div>{contact.address}</div>}
                        {(contact?.postalCode || contact?.city) && (
                          <div>
                            {contact.postalCode} {contact.city}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <ProspectCustomFieldsDisplay fields={contact?.customFields} />
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={14} className="text-muted-foreground" />
                    <span>{contact?.lastContact}</span>
                  </div>
                  {contact?.claimedByEmail && (
                    <div className="flex items-center gap-2">
                      <Icon name="UserCheck" size={14} className="text-muted-foreground" />
                      <span>Assigné à {contact.claimedByEmail}</span>
                    </div>
                  )}
                  <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Tags</p>
                    {Array.isArray(contact?.tags) && contact.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {contact.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Aucun tag assigné
                      </p>
                    )}
                  </div>
                  {matchedCampaigns.length > 0 ? (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Campagnes
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {matchedCampaigns.map((c) => (
                          <span
                            key={c.id}
                            className="px-2 py-0.5 rounded-md bg-secondary/15 text-sm"
                          >
                            {c.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground whitespace-pre-line">
                    {contact?.notes || 'Aucune note pour le moment.'}
                  </p>
                </div>
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-foreground">
                      Historique des appels
                    </h3>
                    {loadingCalls && (
                      <span className="text-xs text-muted-foreground">
                        Chargement...
                      </span>
                    )}
                  </div>
                  {callHistory.length === 0 && !loadingCalls ? (
                    <p className="text-xs text-muted-foreground">
                      Aucun appel enregistré pour ce prospect.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                              Date & heure
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                              Durée
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                              Résultat
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                              Agent
                            </th>
                            <th className="px-3 py-2 text-right font-medium text-muted-foreground uppercase tracking-wider">
                              CR
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {callHistory.map((call) => (
                            <tr key={call._id}>
                              <td className="px-3 py-2">
                                <span className="data-text">
                                  {formatTimestamp(call.startCallAt)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className="data-text">
                                  {formatDuration(call.timeDuration)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${getOutcomeColor(
                                    call.status,
                                  )}`}
                                >
                                  {outcomeLabels[call.status] || call.status}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className="text-foreground">
                                  {call.user?.email || 'N/A'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right">
                                {call.cr && call.cr.trim() !== '' ? (
                                  <button
                                    type="button"
                                    className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-smooth"
                                    title="Voir le compte rendu"
                                    onClick={() => setSelectedCall(call)}
                                  >
                                    <Icon name="Eye" size={14} className="text-primary" />
                                  </button>
                                ) : (
                                  <span
                                    className="inline-flex items-center justify-center p-1.5 rounded"
                                    title="Aucun compte rendu"
                                  >
                                    <Icon name="XCircle" size={14} className="text-error" />
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {selectedCall && (
                    <div className="mt-4 border border-border rounded-lg p-3 bg-muted/40">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-xs font-semibold text-foreground">
                            Compte rendu de l'appel
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {formatTimestamp(selectedCall.startCallAt)} •{' '}
                            {selectedCall.user?.email || 'N/A'}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="text-xs text-primary hover:text-primary/80 font-medium"
                          onClick={() => setSelectedCall(null)}
                        >
                          Revenir à l'historique
                        </button>
                      </div>
                      <p className="text-xs text-foreground whitespace-pre-wrap">
                        {selectedCall.cr}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {showPagesJaunes && (
          <PagesJaunesEnrichModal
            contact={contact}
            onClose={() => setShowPagesJaunes(false)}
            onEnrichmentAttempted={(id) => {
              onEnriched?.(id, { enrichedAt: new Date().toISOString() });
            }}
            onApplied={(id, prospect) => {
              onEnriched?.(id, prospect);
              setShowPagesJaunes(false);
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="bg-card rounded-lg p-4 shadow-elevation-1 hover:shadow-elevation-2 transition-smooth border border-border h-full flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-foreground line-clamp-1 mb-1">
            {contact?.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-1">
            {contact?.position}
          </p>
        </div>

        <div className="flex items-center gap-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getStatusColor(contact?.status)}`}>
            {getStatusLabel(contact?.status)}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${getPriorityColor(contact?.priority)}`}>
            {getPriorityLabel(contact?.priority)}
          </span>
          <button
            type="button"
            className="p-1 rounded hover:bg-muted transition-smooth"
            onClick={onEdit}
          >
            <Icon name="Pencil" size={14} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      <div className="space-y-2 mb-4 flex-1">
        {contact?.company ? (
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Building2" size={14} className="text-muted-foreground flex-shrink-0" />
            <span className="line-clamp-1">{contact.company}</span>
          </div>
        ) : null}
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Icon name="Phone" size={14} className="text-muted-foreground flex-shrink-0" />
          <a href={`tel:${contact?.phone}`} className="hover:text-primary transition-smooth line-clamp-1">
            {contact?.phone}
          </a>
        </div>
        {contact?.mobilePhone && (
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Icon name="Smartphone" size={14} className="text-muted-foreground flex-shrink-0" />
            <a href={`tel:${contact?.mobilePhone}`} className="hover:text-primary transition-smooth line-clamp-1">
              {contact?.mobilePhone}
            </a>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Icon name="Mail" size={14} className="text-muted-foreground flex-shrink-0" />
          <a href={`mailto:${contact?.email}`} className="hover:text-primary transition-smooth line-clamp-1">
            {contact?.email}
          </a>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Briefcase" size={14} className="flex-shrink-0" />
          <span className="line-clamp-1">{contact?.industry}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={14} className="flex-shrink-0" />
          <span>{contact?.lastContact}</span>
        </div>
        {contact?.createdAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="CalendarClock" size={12} className="flex-shrink-0" />
            <span>
              Créé le{' '}
              {new Date(contact.createdAt).toLocaleDateString('fr-FR')}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between mt-1">
          {renderClaimInfo()}
        </div>
        {matchedCampaigns.length > 0 ? (
          <div className="mt-2">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground mb-1">
              Campagnes
            </p>
            <div className="flex flex-wrap gap-1">
              {matchedCampaigns.map((c) => (
                <span
                  key={c.id}
                  className="px-2 py-0.5 rounded-md bg-secondary/15 text-secondary-foreground text-[11px] line-clamp-1"
                  title={
                    Array.isArray(c.tags) && c.tags.length
                      ? `Tags : ${c.tags.join(', ')}`
                      : c.name
                  }
                >
                  {c.name}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Button
            onClick={() => (onCall ? onCall(contact) : handleCall(contact?.phone))}
            variant="success"
            size="sm"
            iconName="Phone"
            fullWidth
          >
            Fixe
          </Button>
          {contact?.mobilePhone && (
            <Button
              onClick={() =>
                onCall
                  ? onCall({ ...contact, phone: contact.mobilePhone })
                  : handleCall(contact.mobilePhone)
              }
              variant="success"
              size="sm"
              iconName="Smartphone"
              fullWidth
            >
              Mobile
            </Button>
          )}
          {!contact?.claimedByEmail && (
            <Button
              onClick={() => onClaim?.(contact.id)}
              variant="outline"
              size="sm"
              iconName="UserPlus"
              fullWidth
            >
              S&apos;assigner le prospect
            </Button>
          )}
        </div>

        <Button
          onClick={() => onMail?.(contact)}
          variant="outline"
          size="sm"
          iconName="Mail"
          fullWidth
          disabled={!contact?.email || mailDisabled}
          title={mailDisabled ? 'Quota e-mail épuisé' : undefined}
        >
          Mail
        </Button>

        {canEnrichProspect(contact) && (
          <div className="mt-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <Button
              onClick={() => setShowPagesJaunes(true)}
              variant="outline"
              size="sm"
              iconName="Search"
              className="flex-1 min-w-[10rem]"
            >
              Enrichir le prospect
            </Button>
            <EnrichedHint contact={contact} />
          </div>
        )}

        <button
          onClick={() => setShowModal(true)}
          className="w-full text-sm text-primary hover:text-primary/80 font-medium transition-smooth flex items-center justify-center gap-1"
        >
          Voir plus
          <Icon name="ChevronRight" size={16} />
        </button>
      </div>
      {showModal && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
        >
          <div
            className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between border-b border-border p-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {contact?.name}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {getStatusLabel(contact?.status)} • {getPriorityLabel(contact?.priority)}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="Phone" size={14} className="text-muted-foreground" />
                  <span>{contact?.phone}</span>
                </div>
                {contact?.mobilePhone && (
                  <div className="flex items-center gap-2">
                    <Icon name="Smartphone" size={14} className="text-muted-foreground" />
                    <span>{contact?.mobilePhone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Icon name="Mail" size={14} className="text-muted-foreground" />
                  <span>{contact?.email}</span>
                </div>
                {canEnrichProspect(contact) && (
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <Button
                      onClick={() => {
                        setShowModal(false);
                        setShowPagesJaunes(true);
                      }}
                      variant="outline"
                      size="sm"
                      iconName="Search"
                    >
                      Enrichir le prospect
                    </Button>
                    <EnrichedHint contact={contact} />
                  </div>
                )}
                {contact?.website && (
                  <div className="flex items-center gap-2">
                    <Icon name="Globe2" size={14} className="text-muted-foreground" />
                    <a
                      href={contact.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-all"
                    >
                      {contact.website}
                    </a>
                  </div>
                )}
                {contact?.ape && (
                  <div className="flex items-center gap-2">
                    <Icon name="Building2" size={14} className="text-muted-foreground" />
                    <span>Code APE : {contact.ape}</span>
                  </div>
                )}
                {contact?.siret && (
                  <div className="flex items-center gap-2">
                    <Icon name="Hash" size={14} className="text-muted-foreground" />
                    <span>SIRET : {contact.siret}</span>
                  </div>
                )}
                {(contact?.address || contact?.postalCode || contact?.city) && (
                  <div className="flex items-start gap-2">
                    <Icon name="MapPin" size={14} className="text-muted-foreground mt-0.5" />
                    <div className="text-xs text-foreground space-y-0.5">
                      {contact?.address && <div>{contact.address}</div>}
                      {(contact?.postalCode || contact?.city) && (
                        <div>
                          {contact.postalCode} {contact.city}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <ProspectCustomFieldsDisplay fields={contact?.customFields} />
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={14} className="text-muted-foreground" />
                  <span>{contact?.lastContact}</span>
                </div>
                {contact?.claimedByEmail && (
                  <div className="flex items-center gap-2">
                    <Icon name="UserCheck" size={14} className="text-muted-foreground" />
                    <span>Assigné à {contact.claimedByEmail}</span>
                  </div>
                )}
                <div className="mt-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Tags</p>
                  {Array.isArray(contact?.tags) && contact.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {contact.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Aucun tag assigné
                    </p>
                  )}
                </div>
                {matchedCampaigns.length > 0 ? (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Campagnes
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {matchedCampaigns.map((c) => (
                        <span
                          key={c.id}
                          className="px-2 py-0.5 rounded-md bg-secondary/15 text-sm"
                        >
                          {c.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                <p className="text-sm text-foreground whitespace-pre-line">
                  {contact?.notes || 'Aucune note pour le moment.'}
                </p>
              </div>
              <div className="pt-3 border-t border-border">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Historique des appels
                  </h3>
                  {loadingCalls && (
                    <span className="text-xs text-muted-foreground">
                      Chargement...
                    </span>
                  )}
                </div>
                {callHistory.length === 0 && !loadingCalls ? (
                  <p className="text-xs text-muted-foreground">
                    Aucun appel enregistré pour ce prospect.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                            Date & heure
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                            Durée
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                            Résultat
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground uppercase tracking-wider">
                            Agent
                          </th>
                          <th className="px-3 py-2 text-right font-medium text-muted-foreground uppercase tracking-wider">
                            CR
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {callHistory.map((call) => (
                          <tr key={call._id}>
                            <td className="px-3 py-2">
                              <span className="data-text">
                                {formatTimestamp(call.startCallAt)}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="data-text">
                                {formatDuration(call.timeDuration)}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${getOutcomeColor(
                                  call.status,
                                )}`}
                              >
                                {outcomeLabels[call.status] || call.status}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="text-foreground">
                                {call.user?.email || 'N/A'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              {call.cr && call.cr.trim() !== '' ? (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-smooth"
                                  title="Voir le compte rendu"
                                  onClick={() => setSelectedCall(call)}
                                >
                                  <Icon name="Eye" size={14} className="text-primary" />
                                </button>
                              ) : (
                                <span
                                  className="inline-flex items-center justify-center p-1.5 rounded"
                                  title="Aucun compte rendu"
                                >
                                  <Icon name="XCircle" size={14} className="text-error" />
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {selectedCall && (
                  <div className="mt-4 border border-border rounded-lg p-3 bg-muted/40">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs font-semibold text-foreground">
                          Compte rendu de l'appel
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {formatTimestamp(selectedCall.startCallAt)} •{' '}
                          {selectedCall.user?.email || 'N/A'}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary hover:text-primary/80 font-medium"
                        onClick={() => setSelectedCall(null)}
                      >
                        Revenir à l'historique
                      </button>
                    </div>
                    <p className="text-xs text-foreground whitespace-pre-wrap">
                      {selectedCall.cr}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showPagesJaunes && (
        <PagesJaunesEnrichModal
          contact={contact}
          onClose={() => setShowPagesJaunes(false)}
          onEnrichmentAttempted={(id) => {
            onEnriched?.(id, { enrichedAt: new Date().toISOString() });
          }}
          onApplied={(id, prospect) => {
            onEnriched?.(id, prospect);
            setShowPagesJaunes(false);
          }}
        />
      )}
    </div>
  );
};

export default ContactCard;